import { createContext, useContext } from "react";

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM v4 — Sports-Essence Design Language
   ─────────────────────────────────────────────────────────────────────────
   Fonts:       Commissioner (display) + Inter (UI) + Inter Tight (condensed) + JetBrains Mono
   Colors:      ac (Action Green primary) + ac2 (Retro Blue secondary) + highlight (bright green)
                gradient (green→blue) + sport tokens (MLB/NFL/NBA)
   Surfaces:    bg → bgAlt → bgCard → bgElevated (4-tier navy-tinted elevation)
   Cards:       Baseball-card thick borders + elevated shadow (sports essence, not literal)
   Type Scale:  xxs=7 xs=10 sm=11 md=13 lg=15 xl=17 xxl=21
   Weights:     normal=500 semi=600 bold=700 extra=800
   Tracking:    tight=-0.5 normal=-0.3 wide=0.3 wider=0.5 widest=1
   Spacing:     2xs=2 xs=4 sm=6 md=8 lg=12 xl=16 2xl=20 3xl=24 4xl=32 5xl=40
   Radius:      sm=4 md=8 lg=12 xl=16 full=999
   Motion:      spring / springDamp / ease / decel / accel
   Elevation:   0-5 layered tinted shadows
   Leading:     tight=1.1 snug=1.25 normal=1.4 relaxed=1.6
   ═══════════════════════════════════════════════════════════════════════════ */

export const DS = {
  ff: {
    display: "'Commissioner','Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    ui: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    cond: "'Inter Tight','Inter',-apple-system,sans-serif",
    article: "'Newsreader','Georgia','Times New Roman',serif",
    mono: "'JetBrains Mono','Fira Code','SF Mono',ui-monospace,monospace",
  },
  font: { xxs:7, xs:10, sm:11, md:13, lg:15, xl:17, xxl:21 },
  fontM: { xxs:9, xs:12, sm:13, md:15, lg:17, xl:19, xxl:23 },
  touch: { min:44 },
  weight: { normal:500, semi:600, bold:700, extra:800 },
  tracking: { tight:-0.5, normal:-0.3, wide:0.3, wider:0.5, widest:1 },
  space: { '2xs':2, xs:4, sm:6, md:8, lg:12, xl:16, '2xl':20, '3xl':24, '4xl':32, '5xl':40 },
  radius: { sm:4, md:8, lg:12, xl:16, full:999 },
  // speed removed — use mt()/mtc() helpers with DS.motion.dur instead
  leading: { tight:1.1, snug:1.25, normal:1.4, relaxed:1.6 },
  elevation: {
    0: 'none',
    1: '0 1px 2px rgba(0,0,0,.06), 0 2px 6px rgba(0,0,0,.08), 0 0 1px rgba(0,0,0,.04)',
    2: '0 2px 4px rgba(0,0,0,.08), 0 4px 12px rgba(0,0,0,.1), 0 0 1px rgba(0,0,0,.04)',
    3: '0 4px 8px rgba(0,0,0,.1), 0 8px 24px rgba(0,0,0,.12), 0 0 1px rgba(0,0,0,.06)',
    4: '0 8px 16px rgba(0,0,0,.12), 0 16px 40px rgba(0,0,0,.16), 0 0 1px rgba(0,0,0,.06)',
    5: '0 12px 24px rgba(0,0,0,.16), 0 24px 56px rgba(0,0,0,.2), 0 0 1px rgba(0,0,0,.08)',
  },
  // Dark theme needs stronger shadow opacities
  elevationDark: {
    0: 'none',
    1: '0 1px 2px rgba(0,0,0,.2), 0 2px 6px rgba(0,0,0,.28), 0 0 1px rgba(0,0,0,.12)',
    2: '0 2px 4px rgba(0,0,0,.28), 0 4px 12px rgba(0,0,0,.35), 0 0 1px rgba(0,0,0,.12)',
    3: '0 4px 8px rgba(0,0,0,.35), 0 8px 24px rgba(0,0,0,.42), 0 0 1px rgba(0,0,0,.18)',
    4: '0 8px 16px rgba(0,0,0,.42), 0 16px 40px rgba(0,0,0,.55), 0 0 1px rgba(0,0,0,.18)',
    5: '0 12px 24px rgba(0,0,0,.48), 0 24px 56px rgba(0,0,0,.6), 0 0 1px rgba(0,0,0,.24)',
  },
  motion: {
    spring: 'cubic-bezier(.34,1.56,.64,1)',
    springDamp: 'cubic-bezier(.25,1,.5,1)',
    ease: 'cubic-bezier(.4,0,.2,1)',
    decel: 'cubic-bezier(0,0,.2,1)',
    accel: 'cubic-bezier(.4,0,1,1)',
    dur: { instant:'.08s', fast:'.15s', base:'.25s', slow:'.4s', slower:'.6s' },
  },
};

export function f(size, mobile) { return mobile ? DS.fontM[size] : DS.font[size]; }

// Motion transition helpers
// Easing convention:
//   Interactive (hover, click, toggle): springDamp — gentle overshoot
//   Data/layout (progress bars, values):  ease     — smooth M3 standard
//   Entrances (fade in, slide in):        decel    — fast start, gentle stop
//   Exits (fade out, collapse):           accel    — gentle start, fast finish
// mt('background','fast','springDamp') → "background .15s cubic-bezier(.25,1,.5,1)"
export function mt(prop='all', dur='fast', easing='ease') {
  return `${prop} ${DS.motion.dur[dur]} ${DS.motion[easing]}`;
}
// mtc(['box-shadow','transform'],'base','ease') → "box-shadow .25s ..., transform .25s ..."
export function mtc(props, dur='fast', easing='ease') {
  return props.map(p => `${p} ${DS.motion.dur[dur]} ${DS.motion[easing]}`).join(', ');
}

// Helper to compute elevation tokens for a theme
function elTokens(isDark) {
  const el = isDark ? DS.elevationDark : DS.elevation;
  return { el0:el[0], el1:el[1], el2:el[2], el3:el[3], el4:el[4], el5:el[5] };
}

export const themes = {
  dark: {
    isLight:false,
    // Deep navy base — navy-tinted near-black
    bg:"#060d18", bgAlt:"#0c1624", bgCard:"#142236", bgHover:"#1a2c42",
    bgHeader:"linear-gradient(180deg,#0c1624 0%,#081018 100%)",
    bgNav:"#081018", bgSticky:"#102030", bgTicker:"#081018", bgTickerHead:"#0e1a2a",
    bgRoundHdr:"#0e1a2a", bgInput:"#0c1624", bgDrop:"#1a2c42",
    bgSurface:"#102030",
    bgOverlay:"rgba(0,4,12,.6)",
    b1:"#1a2c42", b2:"#24384e", b3:"#12202e",
    bFocus:"#69BE2866",
    // Text — AA compliant on both bg and bgCard
    t1:"#e8ecf0", t2:"#9ca8b4", t3:"#8a9cae", t4:"#748ea2",
    tGhost:"#547086",
    // Primary accent: Action Green
    ac:"#69BE28", acSoft:"#69BE2818", acBorder:"#69BE2844",
    acHover:"#7dd33a",
    acGlow:"#69BE2808", acGlow2:"#69BE2812",
    // Highlight: brighter green
    highlight:"#7dd33a", hlSoft:"#7dd33a18",
    // Secondary accent: Retro 80s Seahawk Blue
    ac2:"#0077C0", ac2Soft:"#0077C015", ac2Border:"#0077C040", ac2Hover:"#0090e0",
    // Tertiary accent: Gold
    ac3:"#e5a825", ac3Soft:"#e5a82515", ac3Border:"#e5a82540", ac3Hover:"#f0b830",
    // Brand gradient: Green → Blue
    gradient:"linear-gradient(135deg, #69BE28 0%, #0077C0 100%)",
    gradientSoft:"linear-gradient(135deg, #69BE2810 0%, #0077C010 100%)",
    gradientSubtle:"linear-gradient(135deg, #69BE2808 0%, #0077C006 100%)",
    gradientHover:"linear-gradient(135deg, #69BE2818 0%, #0077C015 100%)",
    gradientGlow:"0 0 12px #69BE2830, 0 0 24px #0077C020",
    // Elevated surface — above bgCard for dropdowns, command palette, popovers
    bgElevated:"#1e3048",
    // NFL shifts to teal to avoid clashing with brand green
    sportMLB:"#dc2626", sportNFL:"#0d9488", sportNBA:"#ea580c",
    kBg:"#0e2010", trBg:"#1c1218",
    success:"#69BE28", good:"#84cc16", warning:"#f59e0b", error:"#ef4444", info:"#0077C0",
    thBg:tc=>tc.bg, thText:()=>"#fff",
    thBgSubtle:tc=>tc.ac+"0a", thBgMedium:tc=>tc.ac+"18",
    thBorderSoft:tc=>tc.ac+"30", thBorderMed:tc=>tc.ac+"44", thBorderHard:tc=>tc.ac+"66",
    faint:"#ffffff06", faint2:"#ffffff0a",
    cardHighlight:"rgba(255,255,255,0.04)", panelHighlight:"rgba(255,255,255,0.03)",
    inputInset:"inset 0 1px 3px rgba(0,0,0,0.3)",
    // Legacy shadow aliases — aligned with elevation tokens
    shadow:"0 4px 24px rgba(0,0,0,.5)",
    shadowSm:"0 1px 2px rgba(0,0,0,.2), 0 2px 6px rgba(0,0,0,.28), 0 0 1px rgba(0,0,0,.12)",
    shadowLg:"0 4px 8px rgba(0,0,0,.35), 0 8px 24px rgba(0,0,0,.42), 0 0 1px rgba(0,0,0,.18)",
    // Elevation tokens
    ...elTokens(true),
  },
  light: {
    isLight:true,
    bg:"#e8ecf0", bgAlt:"#f0f3f6", bgCard:"#ffffff", bgHover:"#e2e8ee",
    bgHeader:"linear-gradient(180deg,#f4f6f8 0%,#eaeff2 100%)",
    bgNav:"#e8ecf0", bgSticky:"#f2f5f8", bgTicker:"#f0f3f6", bgTickerHead:"#e4e8ee",
    bgRoundHdr:"#dce2e8", bgInput:"#f6f8fa", bgDrop:"#ffffff",
    bgSurface:"#f2f5f8",
    bgOverlay:"rgba(0,8,20,.25)",
    b1:"#cedae4", b2:"#b8c8d4", b3:"#e0e8ee",
    bFocus:"#4a9e1a44",
    t1:"#0a1628", t2:"#3a4e62", t3:"#5a7088", t4:"#7a90a4",
    tGhost:"#b8c8d4",
    // Primary accent: brighter green, AA large text on white
    ac:"#3fa016", acSoft:"#3fa01614", acBorder:"#3fa01633",
    acHover:"#4cba22",
    acGlow:"#3fa01606", acGlow2:"#3fa01610",
    highlight:"#006aaa", hlSoft:"#006aaa14",
    // Secondary accent: Retro Blue
    ac2:"#005a94", ac2Soft:"#005a9412", ac2Border:"#005a9430", ac2Hover:"#0077C0",
    // Tertiary accent: Gold
    ac3:"#b8860b", ac3Soft:"#b8860b12", ac3Border:"#b8860b30", ac3Hover:"#d4a017",
    // Brand gradient: Green → Blue
    gradient:"linear-gradient(135deg, #3fa016 0%, #006aaa 100%)",
    gradientSoft:"linear-gradient(135deg, #3fa01608 0%, #006aaa08 100%)",
    gradientSubtle:"linear-gradient(135deg, #3fa01606 0%, #006aaa05 100%)",
    gradientHover:"linear-gradient(135deg, #3fa01615 0%, #006aaa12 100%)",
    gradientGlow:"0 0 12px #3fa01625, 0 0 24px #006aaa18",
    // Elevated surface — above bgCard for dropdowns, command palette, popovers
    bgElevated:"#ffffff",
    // NFL shifts to teal to avoid clashing with brand green
    sportMLB:"#dc2626", sportNFL:"#0d9488", sportNBA:"#ea580c",
    kBg:"#eef6ee", trBg:"#f0f0f8",
    success:"#3fa016", good:"#65a30d", warning:"#d97706", error:"#dc2626", info:"#006aaa",
    thBg:tc=>tc.ac+"18", thText:tc=>tc.ac,
    thBgSubtle:tc=>tc.ac+"0c", thBgMedium:tc=>tc.ac+"18",
    thBorderSoft:tc=>tc.ac+"25", thBorderMed:tc=>tc.ac+"38", thBorderHard:tc=>tc.ac+"55",
    faint:"#00000005", faint2:"#00000008",
    cardHighlight:"rgba(255,255,255,0.7)", panelHighlight:"rgba(255,255,255,0.5)",
    inputInset:"inset 0 1px 3px rgba(0,0,0,0.06)",
    // Legacy shadow aliases — aligned with elevation tokens
    shadow:"0 4px 24px rgba(0,0,0,.08)",
    shadowSm:"0 1px 2px rgba(0,0,0,.06), 0 2px 6px rgba(0,0,0,.08), 0 0 1px rgba(0,0,0,.04)",
    shadowLg:"0 4px 8px rgba(0,0,0,.1), 0 8px 24px rgba(0,0,0,.12), 0 0 1px rgba(0,0,0,.06)",
    // Elevation tokens
    ...elTokens(false),
  }
};

/* ─── Shared style helpers (use with spread) ─── */
export const S = {
  btnPrimary:(T,m)=>({
    padding:m?"10px 16px":"5px 12px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit", fontWeight:DS.weight.semi,
    background:T.ac, color:"#fff", border:"none", borderRadius:DS.radius.sm,
    cursor:"pointer", transition:mt('all','fast','springDamp'), letterSpacing:DS.tracking.wide,
    ...(m&&{minHeight:DS.touch.min}),
  }),
  btnSecondary:(T,m)=>({
    padding:m?"10px 16px":"5px 12px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit", fontWeight:DS.weight.semi,
    background:`${T.ac2}18`, color:T.ac2, border:`1px solid ${T.ac2Border}`,
    borderRadius:DS.radius.sm, cursor:"pointer", transition:mt('all','fast','springDamp'), letterSpacing:DS.tracking.wide,
    ...(m&&{minHeight:DS.touch.min}),
  }),
  btnGhost:(T,m)=>({
    padding:m?"10px 14px":"5px 10px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit", fontWeight:DS.weight.normal,
    background:"transparent", color:T.t3, border:`1px solid ${T.b2}`,
    borderRadius:DS.radius.sm, cursor:"pointer", transition:mt('all','fast','springDamp'),
    ...(m&&{minHeight:DS.touch.min}),
  }),
  tab:(T,a,m)=>({
    padding:m?"10px 14px":"3px 10px", fontSize:m?DS.fontM.sm:DS.font.sm, fontFamily:"inherit",
    fontWeight:a?DS.weight.bold:DS.weight.normal,
    border:a?`1px solid ${T.ac}`:`1px solid ${T.b2}`,
    background:a?T.acSoft:"transparent", color:a?T.ac:T.t3,
    borderRadius:DS.radius.sm, cursor:"pointer", transition:mt('all','fast','springDamp'),
    ...(m&&{minHeight:DS.touch.min}),
  }),
  tabUp:(T,a,m)=>({
    padding:m?"10px 14px":"4px 10px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit",
    fontWeight:a?DS.weight.bold:DS.weight.normal,
    border:a?`1px solid ${T.ac}`:`1px solid ${T.b2}`,
    background:a?T.acSoft:"transparent", color:a?T.ac:T.t3,
    borderRadius:DS.radius.sm, cursor:"pointer", letterSpacing:DS.tracking.wider,
    textTransform:"uppercase", transition:mt('all','fast','springDamp'),
    ...(m&&{minHeight:DS.touch.min}),
  }),
  btnDanger:(T,m)=>({
    padding:m?"10px 14px":"5px 10px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit", fontWeight:DS.weight.semi,
    background:`${T.error}10`, color:T.error, border:"1px solid #ef444433",
    borderRadius:DS.radius.sm, cursor:"pointer", transition:mt('all','fast','springDamp'),
    ...(m&&{minHeight:DS.touch.min}),
  }),
  input:(T,m)=>({
    padding:m?"10px 12px":"4px 8px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit",
    background:T.bgInput, color:T.t1, border:`1px solid ${T.b2}`,
    borderRadius:DS.radius.sm, outline:"none", boxShadow:T.inputInset,
    transition:mtc(['border','box-shadow'],'fast','ease'),
    ...(m&&{minHeight:DS.touch.min}),
  }),
  select:(T,m)=>({
    padding:m?"10px 12px":"4px 8px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit",
    background:T.bgInput, color:T.t1, border:`1px solid ${T.b2}`,
    borderRadius:DS.radius.sm, cursor:"pointer", outline:"none", boxShadow:T.inputInset,
    ...(m&&{minHeight:DS.touch.min}),
  }),
  card:(T)=>({
    background:`linear-gradient(180deg, ${T.bgCard} 0%, ${T.isLight ? T.bgCard : '#0f1c2e'} 100%)`,
    border:`3px solid ${T.b2}`, borderRadius:DS.radius.lg,
    padding:DS.space['3xl'], boxShadow:`inset 0 1px 0 ${T.cardHighlight}, ${T.el2}`,
    transition:mtc(['box-shadow','transform','border-color'],'base','ease'),
  }),
  panel:(T)=>({
    background:T.bgAlt, border:`2px solid ${T.b1}`,
    borderRadius:DS.radius.md, padding:DS.space['2xl'],
    boxShadow:`inset 0 1px 0 ${T.panelHighlight}, ${T.el1}`,
  }),
  sectionLabel:(T,m)=>({
    fontSize:m?DS.fontM.sm:DS.font.sm, fontFamily:DS.ff.display, fontWeight:DS.weight.bold, color:T.t3,
    letterSpacing:DS.tracking.widest, textTransform:"uppercase", marginBottom:DS.space.md,
    lineHeight:DS.leading.snug,
  }),
  pageTitle:(T,m)=>({
    fontSize:m?DS.fontM.xxl:DS.font.xxl, fontFamily:DS.ff.display, fontWeight:DS.weight.extra, color:T.t1,
    letterSpacing:DS.tracking.tight, marginBottom:DS.space.xs,
    lineHeight:DS.leading.tight,
  }),
  badge:(bg,color,m)=>({
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    padding:m?"3px 8px":"1px 5px", fontSize:m?DS.fontM.xs:DS.font.xs, fontWeight:DS.weight.bold,
    background:bg, color:color, borderRadius:DS.radius.sm, lineHeight:1.3,
  }),
  th:(T,m)=>({
    fontSize:m?DS.fontM.xs:DS.font.xs, fontWeight:DS.weight.bold, color:T.t4,
    letterSpacing:DS.tracking.wider, textTransform:"uppercase", padding:m?"8px 8px":"6px 8px",
    borderBottom:`1px solid ${T.b2}`, textAlign:"left", lineHeight:DS.leading.snug,
  }),
  td:(T,m)=>({
    fontSize:m?DS.fontM.sm:DS.font.sm, color:T.t2, padding:m?"6px 8px":"5px 8px",
    borderBottom:`1px solid ${T.b3}`, lineHeight:DS.leading.snug,
  }),
  modalBackdrop:(T)=>({
    position:"fixed", inset:0, background:T.bgOverlay, zIndex:200,
    display:"flex", alignItems:"center", justifyContent:"center",
    animation:`fadeIn ${DS.motion.dur.base} ${DS.motion.decel}`,
    backdropFilter:'blur(4px)',
  }),
  modalBox:(T)=>({
    background:`linear-gradient(180deg, ${T.bgCard} 0%, ${T.isLight ? T.bgCard : '#0f1c2e'} 100%)`,
    border:`3px solid ${T.b2}`, borderRadius:DS.radius.xl,
    boxShadow:`inset 0 1px 0 ${T.cardHighlight}, ${T.el4}`,
    maxWidth:600, width:"90%", maxHeight:"80vh",
    overflow:"auto", padding:DS.space['3xl'],
    animation:`scaleIn ${DS.motion.dur.base} ${DS.motion.springDamp}`,
  }),
  dataLabel:(T,m)=>({
    fontSize:m?DS.fontM.xs:DS.font.xs, fontWeight:DS.weight.bold, color:T.t4,
    letterSpacing:DS.tracking.wider, textTransform:"uppercase", lineHeight:DS.leading.snug,
  }),
  dataValue:(T,m)=>({
    fontSize:m?DS.fontM.xl:DS.font.xl, fontFamily:DS.ff.display, fontWeight:DS.weight.extra, color:T.t1,
    lineHeight:DS.leading.tight, fontVariantNumeric:"tabular-nums",
  }),
  btnGradient:(T,m)=>({
    padding:m?"10px 16px":"5px 12px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit", fontWeight:DS.weight.semi,
    background:T.gradient, color:"#fff", border:"none", borderRadius:DS.radius.sm,
    cursor:"pointer", transition:mt('all','fast','springDamp'), letterSpacing:DS.tracking.wide,
    boxShadow:T.gradientGlow,
    ...(m&&{minHeight:DS.touch.min}),
  }),
  btnAccent:(T,m)=>({
    padding:m?"10px 16px":"5px 12px", fontSize:m?DS.fontM.md:DS.font.md, fontFamily:"inherit", fontWeight:DS.weight.semi,
    background:T.ac3, color:T.isLight?"#fff":"#1a1200", border:"none", borderRadius:DS.radius.sm,
    cursor:"pointer", transition:mt('all','fast','springDamp'), letterSpacing:DS.tracking.wide,
    ...(m&&{minHeight:DS.touch.min}),
  }),
  gradientText:(T)=>({
    background:T.gradient, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
    backgroundClip:"text",
  }),
  accentGlow:(T, intensity = 1)=>({
    boxShadow:`0 0 ${8 * intensity}px ${T.ac}${intensity > 0.5 ? '40' : '20'}, 0 0 ${16 * intensity}px ${T.ac}${intensity > 0.5 ? '20' : '10'}`,
  }),
};

export const Tx = createContext(themes.dark);
export function useT() { return useContext(Tx); }

// Team colors
export const TC=[
  {bg:"#1e1e34",ac:"#e94560"},{bg:"#1a2844",ac:"#4a8fe7"},{bg:"#1f303f",ac:"#66c0f4"},
  {bg:"#332244",ac:"#a855f7"},{bg:"#224236",ac:"#34d399"},{bg:"#402020",ac:"#f87171"},
  {bg:"#203442",ac:"#38bdf8"},{bg:"#352c18",ac:"#fbbf24"},{bg:"#1e3420",ac:"#4ade80"},
  {bg:"#332434",ac:"#f472b6"}
];

// Position colors — AA compliant per theme
export const PCD={SP:"#2563eb",RP:"#60a5fa",OF:"#22c55e",SS:"#f59e0b","1B":"#ef4444","2B":"#8b5cf6","3B":"#ec4899",C:"#14b8a6",DH:"#9ca3af"};
export const PCL={SP:"#2563eb",RP:"#2563eb",OF:"#047857",SS:"#b45309","1B":"#dc2626","2B":"#7c3aed","3B":"#db2777",C:"#0d9488",DH:"#6b7280"};

export function gpc(pos, isLight) {
  const m = isLight ? PCL : PCD;
  if (!pos) return isLight ? "#5a7088" : "#8a9cae";
  for (const p of ["SP","RP","OF","SS","1B","2B","3B","C","DH"]) if (pos.includes(p)) return m[p];
  return isLight ? "#5a7088" : "#8a9cae";
}

// 5-tier value color scale: ratio 0 = best, 1 = worst
export function valueColor(ratio, T) {
  if (ratio <= 0.2) return T.success;
  if (ratio <= 0.4) return T.good;
  if (ratio <= 0.6) return T.t3;
  if (ratio <= 0.8) return T.warning;
  return T.error;
}

/**
 * Apply team color theming — swaps accent colors on a base theme.
 * Only touches ac/ac2/gradient/highlight/success tokens. Surfaces + text stay the same.
 * @param {object} baseTheme - themes.dark or themes.light
 * @param {string} primary - team primary hex color (e.g., '#BD3039')
 * @param {string} secondary - team secondary hex color (e.g., '#0C2340')
 * @returns {object} new theme with team-colored accents
 */
export function applyTeamColors(baseTheme, primary, secondary) {
  if (!primary || !secondary) return baseTheme;
  const isDark = !baseTheme.isLight;
  // Lighten a hex color by a percentage for hover states
  const lighten = (hex, pct = 0.15) => {
    const n = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, ((n >> 16) & 0xff) + Math.round(255 * pct));
    const g = Math.min(255, ((n >> 8) & 0xff) + Math.round(255 * pct));
    const b = Math.min(255, (n & 0xff) + Math.round(255 * pct));
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  };
  // For light theme, darken instead of lighten for hover
  const hoverShift = isDark ? lighten(primary, 0.12) : lighten(primary, -0.08);
  const hoverShift2 = isDark ? lighten(secondary, 0.12) : lighten(secondary, -0.08);
  return {
    ...baseTheme,
    // Primary accent
    ac: primary, acSoft: primary + '18', acBorder: primary + '44',
    acHover: hoverShift, acGlow: primary + '08', acGlow2: primary + '12',
    bFocus: primary + '66',
    // Highlight
    highlight: isDark ? lighten(primary, 0.1) : primary,
    hlSoft: (isDark ? lighten(primary, 0.1) : primary) + '18',
    // Secondary accent
    ac2: secondary, ac2Soft: secondary + '15', ac2Border: secondary + '40', ac2Hover: hoverShift2,
    // Gradient
    gradient: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
    gradientSoft: `linear-gradient(135deg, ${primary}10 0%, ${secondary}10 100%)`,
    gradientSubtle: `linear-gradient(135deg, ${primary}08 0%, ${secondary}06 100%)`,
    gradientHover: `linear-gradient(135deg, ${primary}18 0%, ${secondary}15 100%)`,
    gradientGlow: `0 0 12px ${primary}30, 0 0 24px ${secondary}20`,
    // Success matches primary for consistency
    success: primary,
    // Info matches secondary
    info: secondary,
  };
}
